import { useState, useEffect } from 'react'
import { usePage, router } from '@inertiajs/react'
import type CrmPeopleIndividualDonor from '@/types/serializers/CrmPeopleIndividualDonor'
import type CrmPeopleOrganizationDonor from '@/types/serializers/CrmPeopleOrganizationDonor'
import type CrmPeopleNote from '@/types/serializers/CrmPeopleNote'
import type CrmPeopleContact from '@/types/serializers/CrmPeopleContact'
import type { DonorRow } from './DonorColumns'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Target, FileText, MoreHorizontal, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { DonorDetailOverview } from './DonorDetailOverview'
import { NotesTab } from './NotesTab'
import { ContactsTab } from './ContactsTab'
import { DonationsTab } from './DonationsTab'
import { TasksSlot } from '@/components/work/TasksSlot'

interface DonorDetail {
  donor: CrmPeopleIndividualDonor | CrmPeopleOrganizationDonor
  donor_type: 'individual' | 'organization'
  notes: CrmPeopleNote[]
  contacts: (CrmPeopleContact & { notes?: CrmPeopleNote[] })[] | null
}

interface DonorDetailSheetProps {
  donor: DonorRow | null
  onOpenChange: (open: boolean) => void
}

function PlaceholderTab({ icon: Icon, message }: { icon: React.ComponentType<{ className?: string }>; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
      <Icon className="h-10 w-10 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

export function DonorDetailSheet({ donor, onOpenChange }: DonorDetailSheetProps) {
  const [detail, setDetail] = useState<DonorDetail | null>(null)
  const [activeTab, setActiveTab] = useState('apercu')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [withDiscardedContacts, setWithDiscardedContacts] = useState(false)
  const { auth } = usePage().props
  const currentUserId = (auth as { user?: { id: number } }).user?.id ?? 0

  const isArchived = detail?.donor?.discarded === true

  // When donor changes (row clicked), load the detail via plain fetch
  useEffect(() => {
    if (!donor) {
      setDetail(null)
      setIsEditing(false)
      setWithDiscardedContacts(false)
      return
    }
    setLoading(true)
    setActiveTab('apercu')
    setIsEditing(false)
    setWithDiscardedContacts(false)

    const controller = new AbortController()
    fetch(`/people/detail/${donor.donor_type}/${donor.id}`, {
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load detail')
        return res.json()
      })
      .then((data) => setDetail(data))
      .catch((err) => {
        if (err.name !== 'AbortError') console.error(err)
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [donor?.id])

  // Reload detail data (called after note/contact mutations in child components)
  function reloadDetail(discarded?: boolean) {
    if (!donor) return
    const withDiscarded = discarded ?? withDiscardedContacts
    const url = `/people/detail/${donor.donor_type}/${donor.id}` +
      (withDiscarded ? '?contacts_with_discarded=true' : '')
    fetch(url, {
      headers: { 'Accept': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => setDetail(data))
      .catch(console.error)
  }

  // Handle archived toggle from ContactsTab
  function handleContactsArchivedToggle(checked: boolean) {
    setWithDiscardedContacts(checked)
    reloadDetail(checked)
  }

  function handleArchive() {
    const url = detail?.donor_type === 'individual'
      ? `/people/individual_donors/${donor!.id}/archive`
      : `/people/organization_donors/${donor!.id}/archive`
    router.patch(url, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setConfirmArchive(false)
        reloadDetail()
        toast.success('Donnateur archive avec succes')
      }
    })
  }

  function handleRestore() {
    const url = detail?.donor_type === 'individual'
      ? `/people/individual_donors/${donor!.id}/restore`
      : `/people/organization_donors/${donor!.id}/restore`
    router.patch(url, {}, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        reloadDetail()
        toast.success('Donnateur restaure avec succes')
      }
    })
  }

  function handleOpenChange(open: boolean) {
    onOpenChange(open)
  }

  // Prefer the fetched detail's name (the row's name is empty for off-page
  // deep-link rows); fall back to the row name, then a non-blank placeholder so
  // the visible/accessible title is never empty.
  const fetchedName = detail
    ? detail.donor_type === 'organization'
      ? (detail.donor as CrmPeopleOrganizationDonor).organization_name
      : [
          (detail.donor as CrmPeopleIndividualDonor).first_name,
          (detail.donor as CrmPeopleIndividualDonor).last_name,
        ]
          .filter(Boolean)
          .join(' ')
    : null
  const sheetTitle = fetchedName || donor?.name || 'Detail du donnateur'

  return (
    <>
      <Sheet open={donor !== null} onOpenChange={handleOpenChange}>
        <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto">
          <SheetHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <SheetTitle>{sheetTitle}</SheetTitle>
                {donor && (
                  <SheetDescription>
                    {donor.donor_type === 'individual' ? 'Donnateur individuel' : 'Organisation donatrice'}
                  </SheetDescription>
                )}
              </div>
              {donor && detail && (
                <div className="flex items-center gap-1 shrink-0">
                  {!isEditing && !isArchived && (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Modifier
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isArchived ? (
                        <DropdownMenuItem onClick={handleRestore}>
                          Restaurer
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setConfirmArchive(true)}
                        >
                          Archiver
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </SheetHeader>

          {donor && (
            <div className="p-4">
              {loading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full">
                    <TabsTrigger value="apercu" className="flex-1">Apercu</TabsTrigger>
                    {donor.donor_type === 'organization' && (
                      <TabsTrigger value="contacts" className="flex-1">Contacts</TabsTrigger>
                    )}
                    <TabsTrigger value="notes" className="flex-1">Notes</TabsTrigger>
                    <TabsTrigger value="dons" className="flex-1">Dons</TabsTrigger>
                    <TabsTrigger value="taches" className="flex-1">Tâches</TabsTrigger>
                    <TabsTrigger value="opportunites" className="flex-1">Opportunites</TabsTrigger>
                    <TabsTrigger value="documents" className="flex-1">Documents</TabsTrigger>
                  </TabsList>

                  <TabsContent value="apercu" className="mt-4">
                    {detail ? (
                      <div className={isArchived ? 'opacity-60' : ''}>
                        {isArchived && (
                          <div className="bg-muted border rounded-md px-3 py-2 mb-4 text-sm text-muted-foreground">
                            Ce donnateur est archive.
                          </div>
                        )}
                        <DonorDetailOverview
                          donor={detail.donor}
                          donorType={detail.donor_type}
                          isEditing={isEditing}
                          onEditSaved={() => { setIsEditing(false); reloadDetail() }}
                          onEditCancelled={() => setIsEditing(false)}
                          onStatusChanged={reloadDetail}
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    )}
                  </TabsContent>

                  {donor.donor_type === 'organization' && (
                    <TabsContent value="contacts" className="mt-4">
                      {detail ? (
                        <ContactsTab
                          contacts={detail.contacts ?? []}
                          organizationDonorId={donor.id}
                          currentUserId={currentUserId}
                          withDiscarded={withDiscardedContacts}
                          onReloadDetail={reloadDetail}
                          onArchivedToggle={handleContactsArchivedToggle}
                        />
                      ) : (
                        <div className="space-y-3">
                          <Skeleton className="h-10 w-full" />
                          <Skeleton className="h-10 w-full" />
                        </div>
                      )}
                    </TabsContent>
                  )}

                  <TabsContent value="notes" className="mt-4">
                    {detail ? (
                      <NotesTab
                        notes={detail.notes}
                        noteableType={donor.donor_type === 'individual' ? 'individual_donor' : 'organization_donor'}
                        noteableId={donor.id}
                        currentUserId={currentUserId}
                        onReloadDetail={reloadDetail}
                      />
                    ) : (
                      <div className="space-y-3">
                        <Skeleton className="h-20 w-full" />
                        <Skeleton className="h-16 w-full" />
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="dons" className="mt-4">
                    <DonationsTab donor={donor} isDonorDiscarded={isArchived} />
                  </TabsContent>

                  <TabsContent value="taches" className="mt-4">
                    <TasksSlot
                      subjectType={
                        donor.donor_type === 'individual'
                          ? 'CrmPeople::IndividualDonor'
                          : 'CrmPeople::OrganizationDonor'
                      }
                      subjectId={donor.id}
                      isSubjectClosed={isArchived}
                    />
                  </TabsContent>

                  <TabsContent value="opportunites" className="mt-4">
                    <PlaceholderTab icon={Target} message="Les opportunites seront disponibles prochainement." />
                  </TabsContent>

                  <TabsContent value="documents" className="mt-4">
                    <PlaceholderTab icon={FileText} message="Les documents seront disponibles prochainement." />
                  </TabsContent>
                </Tabs>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver ce donnateur</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir archiver ce donnateur? Il ne sera plus visible dans la liste principale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
